"use client";

import { useCallback, useRef } from "react";
import type { Editor } from "@tiptap/react";
import { wrapImagesWithLinks } from "../utils";
import type {
  ResponsiveSizes,
  ResponsiveColumnWidths,
  ResponsivePadding,
  ResponsiveCellStyle,
  PaddingValues,
} from "../types";

const defaultPadding: PaddingValues = { top: "8", right: "12", bottom: "8", left: "12" };

// Track initial color values to only apply if changed
interface InitialColors {
  columnBackgroundColor: string;
  columnTextColor: string;
  columnBorderColor: string;
  cellBackgroundColor: string;
  cellTextColor: string;
  cellBorderColor: string;
}

interface TableState {
  tableRows: number;
  tableCols: number;
  tableWithHeader: boolean;
  tableBorderColor: string;
  tableBorderWidth: number;
  currentTableBorderColor: string;
  currentTableBorderWidth: number;
  currentTableBorderRadius: number;
  cellBackgroundColor: string;
  cellBorderColor: string;
  cellBorderWidth: number;
  cellTextColor: string;
  cellTextAlign: "left" | "center" | "right";
  cellVerticalAlign: "top" | "middle" | "bottom";
  cellPadding: ResponsivePadding;
  cellFontSize: string;
  cellFontWeight: string;
  responsiveTableWidths: ResponsiveSizes;
  tableWidthType: "percent" | "pixel";
  enableHorizontalScroll: boolean;
  tableAlignment: "left" | "center" | "right";
  // Column style state
  columnIndex: number;
  columnWidths: ResponsiveColumnWidths;
  columnWidthType: "percent" | "pixel" | "auto";
  columnTextAlign: "left" | "center" | "right";
  columnVerticalAlign: "top" | "middle" | "bottom";
  columnPadding: ResponsivePadding;
  columnBackgroundColor: string;
  columnTextColor: string;
  columnBorderColor: string;
  // Responsive cell styles
  responsiveCellStyles: ResponsiveCellStyle;
}

interface TableStateSetters {
  setTableRows: (value: number) => void;
  setTableCols: (value: number) => void;
  setTableWithHeader: (value: boolean) => void;
  setTableBorderColor: (value: string) => void;
  setTableBorderWidth: (value: number) => void;
  setShowTableModal: (value: boolean) => void;
  setCurrentTableBorderColor: (value: string) => void;
  setCurrentTableBorderWidth: (value: number) => void;
  setCurrentTableBorderRadius: (value: number) => void;
  setShowTableSettingsModal: (value: boolean) => void;
  setCellBackgroundColor: (value: string) => void;
  setCellBorderColor: (value: string) => void;
  setCellBorderWidth: (value: number) => void;
  setCellTextColor: (value: string) => void;
  setCellTextAlign: (value: "left" | "center" | "right") => void;
  setCellVerticalAlign: (value: "top" | "middle" | "bottom") => void;
  setCellPadding: (value: ResponsivePadding) => void;
  setCellFontSize: (value: string) => void;
  setCellFontWeight: (value: string) => void;
  setShowCellStyleModal: (value: boolean) => void;
  setResponsiveTableWidths: (value: ResponsiveSizes) => void;
  setTableWidthType: (value: "percent" | "pixel") => void;
  setEnableHorizontalScroll: (value: boolean) => void;
  setTableAlignment: (value: "left" | "center" | "right") => void;
  setShowResponsiveTableSize: (value: boolean) => void;
  // Column style setters
  setColumnIndex: (value: number) => void;
  setColumnWidths: (value: ResponsiveColumnWidths) => void;
  setColumnWidthType: (value: "percent" | "pixel" | "auto") => void;
  setColumnTextAlign: (value: "left" | "center" | "right") => void;
  setColumnVerticalAlign: (value: "top" | "middle" | "bottom") => void;
  setColumnPadding: (value: ResponsivePadding) => void;
  setColumnBackgroundColor: (value: string) => void;
  setColumnTextColor: (value: string) => void;
  setColumnBorderColor: (value: string) => void;
  setShowColumnStyleModal: (value: boolean) => void;
  // Responsive cell styles setters
  setResponsiveCellStyles: (value: ResponsiveCellStyle) => void;
  setShowResponsiveCellStyleModal: (value: boolean) => void;
}

interface UseTableHandlersProps {
  editor: Editor | null;
  state: TableState;
  setters: TableStateSetters;
  onChange?: (html: string) => void;
}

export const useTableHandlers = ({ editor, state, setters, onChange }: UseTableHandlersProps) => {
  // Track initial color values to only apply colors if they've been changed
  const initialColorsRef = useRef<InitialColors>({
    columnBackgroundColor: "",
    columnTextColor: "",
    columnBorderColor: "",
    cellBackgroundColor: "",
    cellTextColor: "",
    cellBorderColor: "",
  });

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertTable({
        rows: state.tableRows,
        cols: state.tableCols,
        withHeaderRow: state.tableWithHeader,
      })
      .run();

    setTimeout(() => {
      editor
        .chain()
        .focus()
        .updateAttributes("table", {
          borderColor: state.tableBorderColor,
          borderWidth: state.tableBorderWidth,
        })
        .run();
    }, 0);

    setters.setShowTableModal(false);
    setters.setTableRows(3);
    setters.setTableCols(3);
    setters.setTableWithHeader(true);
    setters.setTableBorderColor("#000000");
    setters.setTableBorderWidth(1);
  }, [editor, state.tableRows, state.tableCols, state.tableWithHeader, state.tableBorderColor, state.tableBorderWidth, setters]);

  const openTableSettings = useCallback(() => {
    if (!editor) return;
    const tableAttrs = editor.getAttributes("table");
    setters.setCurrentTableBorderColor(tableAttrs.borderColor || "#000000");
    setters.setCurrentTableBorderWidth(tableAttrs.borderWidth || 1);
    setters.setCurrentTableBorderRadius(tableAttrs.borderRadius || 0);
    setters.setShowTableSettingsModal(true);
  }, [editor, setters]);

  const applyTableSettings = useCallback(() => {
    if (!editor) return;

    const tableAttrs = editor.getAttributes("table");
    const existingStyle = tableAttrs.style || "";
    let newStyle = existingStyle
      .replace(/border-radius:\s*\d+px;?\s*/gi, "")
      .replace(/overflow:\s*hidden;?\s*/gi, "")
      .replace(/border-collapse:\s*\w+;?\s*/gi, "")
      .replace(/border-spacing:\s*\d+px;?\s*/gi, "")
      .trim();

    if (state.currentTableBorderRadius > 0) {
      newStyle = newStyle
        ? `${newStyle} border-radius: ${state.currentTableBorderRadius}px; overflow: hidden; border-collapse: separate; border-spacing: 0;`
        : `border-radius: ${state.currentTableBorderRadius}px; overflow: hidden; border-collapse: separate; border-spacing: 0;`;
    }

    editor
      .chain()
      .focus()
      .updateAttributes("table", {
        borderColor: state.currentTableBorderColor,
        borderWidth: state.currentTableBorderWidth,
        borderRadius: state.currentTableBorderRadius,
        style: newStyle || null,
      })
      .run();

    // Update cell borders
    const { selection } = editor.state;
    const { $from } = selection;

    let tableNode = null;
    let tablePos = 0;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "table") {
        tableNode = node;
        tablePos = $from.before(depth);
        break;
      }
    }

    if (tableNode) {
      const rows: Array<{ node: any; pos: number; cells: Array<{ node: any; pos: number }> }> = [];
      let currentRow: { node: any; pos: number; cells: Array<{ node: any; pos: number }> } | null = null;

      tableNode.descendants((node, pos) => {
        if (node.type.name === "tableRow") {
          currentRow = { node, pos, cells: [] };
          rows.push(currentRow);
        } else if ((node.type.name === "tableCell" || node.type.name === "tableHeader") && currentRow) {
          currentRow.cells.push({ node, pos });
        }
      });

      const totalRows = rows.length;
      const totalCols = rows[0]?.cells.length || 0;
      const radius = state.currentTableBorderRadius;
      const borderWidth = state.currentTableBorderWidth;
      const borderColor = state.currentTableBorderColor;

      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          rows.forEach((row, rowIndex) => {
            row.cells.forEach((cell, colIndex) => {
              const absolutePos = tablePos + cell.pos + 1;
              const borderStyles: string[] = [];

              if (radius > 0) {
                borderStyles.push(`border-right: ${borderWidth}px solid ${borderColor}`);
                borderStyles.push(`border-bottom: ${borderWidth}px solid ${borderColor}`);
                if (rowIndex === 0) borderStyles.push(`border-top: ${borderWidth}px solid ${borderColor}`);
                if (colIndex === 0) borderStyles.push(`border-left: ${borderWidth}px solid ${borderColor}`);
                if (rowIndex === 0 && colIndex === 0) borderStyles.push(`border-top-left-radius: ${radius}px`);
                if (rowIndex === 0 && colIndex === totalCols - 1) borderStyles.push(`border-top-right-radius: ${radius}px`);
                if (rowIndex === totalRows - 1 && colIndex === 0) borderStyles.push(`border-bottom-left-radius: ${radius}px`);
                if (rowIndex === totalRows - 1 && colIndex === totalCols - 1) borderStyles.push(`border-bottom-right-radius: ${radius}px`);
              }

              const existingCellStyle = cell.node.attrs.style || "";
              const cleanedCellStyle = existingCellStyle
                .replace(/border-top-left-radius:\s*\d+px;?\s*/gi, "")
                .replace(/border-top-right-radius:\s*\d+px;?\s*/gi, "")
                .replace(/border-bottom-left-radius:\s*\d+px;?\s*/gi, "")
                .replace(/border-bottom-right-radius:\s*\d+px;?\s*/gi, "")
                .replace(/border-right:\s*[^;]+;?\s*/gi, "")
                .replace(/border-left:\s*[^;]+;?\s*/gi, "")
                .replace(/border-top:\s*[^;]+;?\s*/gi, "")
                .replace(/border-bottom:\s*[^;]+;?\s*/gi, "")
                .replace(/border-color:\s*[^;]+;?\s*/gi, "")
                .replace(/border-width:\s*[^;]+;?\s*/gi, "")
                .trim();

              const cellStyleStr = borderStyles.join("; ");
              const finalCellStyle = cleanedCellStyle
                ? cellStyleStr
                  ? `${cleanedCellStyle}; ${cellStyleStr}`
                  : cleanedCellStyle
                : cellStyleStr;

              tr.setNodeMarkup(absolutePos, undefined, {
                ...cell.node.attrs,
                borderColor: radius > 0 ? null : state.currentTableBorderColor,
                borderWidth: radius > 0 ? null : state.currentTableBorderWidth,
                style: finalCellStyle || null,
              });
            });
          });
          return true;
        })
        .run();
    }

    setters.setShowTableSettingsModal(false);
  }, [editor, state.currentTableBorderColor, state.currentTableBorderWidth, state.currentTableBorderRadius, setters]);

  // Helper function to parse style string into object
  const parseStyleString = (styleStr: string | null | undefined): Record<string, string> => {
    if (!styleStr) return {};
    const styles: Record<string, string> = {};
    styleStr.split(";").forEach((part) => {
      const [key, value] = part.split(":").map((s) => s?.trim());
      if (key && value) {
        // Convert kebab-case to camelCase
        const camelKey = key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
        styles[camelKey] = value.replace(/!important/gi, "").trim();
      }
    });
    return styles;
  };

  // Helper to extract numeric value from CSS value
  const extractNumericValue = (value: string | undefined): string => {
    if (!value) return "";
    const match = value.match(/^(\d+)/);
    return match ? match[1] : "";
  };

  // Helper to parse padding string into PaddingValues
  const parsePaddingString = (paddingStr: string | undefined): PaddingValues | null => {
    if (!paddingStr) return null;
    const values = paddingStr.split(/\s+/).map((v) => extractNumericValue(v));
    if (values.length === 1) {
      return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
    } else if (values.length === 2) {
      return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
    } else if (values.length === 3) {
      return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
    } else if (values.length === 4) {
      return { top: values[0], right: values[1], bottom: values[2], left: values[3] };
    }
    return null;
  };

  const openCellStyleModal = useCallback(() => {
    if (!editor) return;
    
    // Try to get attributes from tableCell first, then tableHeader
    let cellAttrs = editor.getAttributes("tableCell");
    if (!cellAttrs || Object.keys(cellAttrs).length === 0) {
      cellAttrs = editor.getAttributes("tableHeader");
    }
    
    // Also parse the style string to get computed values
    const styleObj = parseStyleString(cellAttrs?.style);
    
    // Get values with fallbacks from style string and then defaults
    const bgColor = cellAttrs?.backgroundColor || styleObj.backgroundColor || "#ffffff";
    const borderColor = cellAttrs?.borderColor || styleObj.borderColor || "#000000";
    const borderWidth = cellAttrs?.borderWidth || extractNumericValue(styleObj.borderWidth) || "1";
    const textColor = cellAttrs?.textColor || styleObj.color || "#000000";
    const textAlign = cellAttrs?.textAlign || styleObj.textAlign || "left";
    const verticalAlign = cellAttrs?.verticalAlign || styleObj.verticalAlign || "middle";
    const fontSize = cellAttrs?.fontSize?.toString() || extractNumericValue(styleObj.fontSize) || "";
    const fontWeight = cellAttrs?.fontWeight || styleObj.fontWeight || "";
    
    setters.setCellBackgroundColor(bgColor);
    setters.setCellBorderColor(borderColor);
    setters.setCellBorderWidth(typeof borderWidth === "string" ? parseInt(borderWidth) || 1 : borderWidth);
    setters.setCellTextColor(textColor);
    setters.setCellTextAlign(textAlign as "left" | "center" | "right");
    setters.setCellVerticalAlign(verticalAlign as "top" | "middle" | "bottom");
    setters.setCellFontSize(fontSize);
    setters.setCellFontWeight(fontWeight);
    
    // Parse existing padding or use defaults
    const defaultResponsivePadding: ResponsivePadding = {
      xs: { ...defaultPadding },
      sm: { ...defaultPadding },
      md: { ...defaultPadding },
      lg: { ...defaultPadding },
      xl: { ...defaultPadding },
    };
    
    // Try to get padding from style string
    const parsedPadding = parsePaddingString(styleObj.padding);
    if (parsedPadding) {
      defaultResponsivePadding.xs = parsedPadding;
    }
    
    // Override with saved responsive styles if available
    if (cellAttrs?.["data-responsive-styles"]) {
      try {
        const savedStyles = JSON.parse(cellAttrs["data-responsive-styles"]);
        if (savedStyles.padding) {
          Object.keys(savedStyles.padding).forEach((key) => {
            if (defaultResponsivePadding[key as keyof ResponsivePadding] && savedStyles.padding[key]) {
              defaultResponsivePadding[key as keyof ResponsivePadding] = {
                ...defaultPadding,
                ...savedStyles.padding[key],
              };
            }
          });
        }
      } catch {
        // Use defaults
      }
    }
    
    setters.setCellPadding(defaultResponsivePadding);
    setters.setShowCellStyleModal(true);
  }, [editor, setters]);

  const applyCellStyles = useCallback(() => {
    if (!editor) return;
    
    // Build padding string from xs breakpoint (base)
    const basePadding = state.cellPadding.xs || defaultPadding;
    const paddingStr = `${basePadding.top}px ${basePadding.right}px ${basePadding.bottom}px ${basePadding.left}px`;
    
    // Store responsive padding data
    const responsiveData = JSON.stringify({
      padding: state.cellPadding,
    });
    
    editor
      .chain()
      .focus()
      .setCellAttribute("backgroundColor", state.cellBackgroundColor)
      .setCellAttribute("borderColor", state.cellBorderColor)
      .setCellAttribute("borderWidth", state.cellBorderWidth)
      .setCellAttribute("textColor", state.cellTextColor)
      .setCellAttribute("textAlign", state.cellTextAlign)
      .setCellAttribute("verticalAlign", state.cellVerticalAlign)
      .setCellAttribute("fontSize", state.cellFontSize || null)
      .setCellAttribute("fontWeight", state.cellFontWeight || null)
      .setCellAttribute("padding", paddingStr)
      .setCellAttribute("data-responsive-styles", responsiveData)
      .run();
    
    // Generate CSS for responsive padding
    const { selection } = editor.state;
    const { $from } = selection;
    
    // Find the current cell and add unique class
    let cellNode = null;
    let cellPos = 0;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
        cellNode = node;
        cellPos = $from.before(depth);
        break;
      }
    }
    
    if (cellNode) {
      const uniqueClass = `cell-style-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
      
      // Add class to cell
      editor.chain().focus().setCellAttribute("class", uniqueClass).run();
      
      // Generate responsive CSS
      let css = `
  .${uniqueClass} {
    padding: ${state.cellPadding.xs.top}px ${state.cellPadding.xs.right}px ${state.cellPadding.xs.bottom}px ${state.cellPadding.xs.left}px !important;
  }`;
      
      const breakpoints = [
        { key: "sm", width: "1080px" },
        { key: "md", width: "1230px" },
        { key: "lg", width: "1430px" },
        { key: "xl", width: "1750px" },
      ] as const;
      
      breakpoints.forEach(({ key, width }) => {
        const bp = state.cellPadding[key];
        if (bp) {
          css += `
  @media (min-width: ${width}) {
    .${uniqueClass} {
      padding: ${bp.top}px ${bp.right}px ${bp.bottom}px ${bp.left}px !important;
    }
  }`;
        }
      });
      
      // Add CSS to document
      let currentHtml = editor.getHTML();
      const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
      const styleMatch = currentHtml.match(styleTagRegex);
      
      const htmlWithStyles = styleMatch
        ? currentHtml.replace(styleTagRegex, `<style>${styleMatch[1]}${css}</style>`)
        : `<style>${css}</style>${currentHtml}`;
      
      editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
      const finalHtml = wrapImagesWithLinks(editor.getHTML());
      onChange?.(finalHtml);
    }
    
    // Close modal first
    setters.setShowCellStyleModal(false);
    
    // Re-focus the editor after a short delay to maintain selection state
    setTimeout(() => {
      editor.chain().focus().run();
    }, 100);
  }, [editor, state.cellBackgroundColor, state.cellBorderColor, state.cellBorderWidth, state.cellTextColor, state.cellTextAlign, state.cellVerticalAlign, state.cellFontSize, state.cellFontWeight, state.cellPadding, setters, onChange]);

  // Column Style Modal Handlers
  const openColumnStyleModal = useCallback((columnIndex: number) => {
    if (!editor) return;
    
    // Get header cell attributes for this column
    const { selection } = editor.state;
    const { $from } = selection;
    
    // Find table and get column header cell
    let tableNode = null;
    let tablePos = 0;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "table") {
        tableNode = node;
        tablePos = $from.before(depth);
        break;
      }
    }
    
    // Default values
    const defaultColumnWidths: ResponsiveColumnWidths = { xs: "", sm: "", md: "", lg: "", xl: "" };
    const defaultColumnPadding: ResponsivePadding = {
      xs: { ...defaultPadding },
      sm: { ...defaultPadding },
      md: { ...defaultPadding },
      lg: { ...defaultPadding },
      xl: { ...defaultPadding },
    };
    
    setters.setColumnIndex(columnIndex);
    setters.setColumnWidths(defaultColumnWidths);
    setters.setColumnWidthType("auto");
    setters.setColumnTextAlign("left");
    setters.setColumnVerticalAlign("middle");
    setters.setColumnPadding(defaultColumnPadding);
    setters.setColumnBackgroundColor("");
    setters.setColumnTextColor("");
    setters.setColumnBorderColor("");
    
    // Try to get existing values from header cell or first row cell
    if (tableNode) {
      let targetRow: any = null;
      let rowIndex = 0;
      
      // Find first row (header or regular)
      tableNode.descendants((node) => {
        if (node.type.name === "tableRow" && !targetRow) {
          targetRow = node;
          return false;
        }
        return true;
      });
      
      if (targetRow) {
        let colIdx = 0;
        targetRow.descendants((node: any) => {
          if (node.type.name === "tableHeader" || node.type.name === "tableCell") {
            if (colIdx === columnIndex) {
              const nodeAttrs = node.attrs || {};
              const styleObj = parseStyleString(nodeAttrs.style);
              
              // Parse column widths from data attribute
              if (nodeAttrs["data-column-widths"]) {
                try {
                  const widthData = JSON.parse(nodeAttrs["data-column-widths"]);
                  setters.setColumnWidths(widthData.widths || defaultColumnWidths);
                  setters.setColumnWidthType(widthData.type || "auto");
                } catch {
                  // Use defaults
                }
              }
              
              // Parse responsive styles from data attribute
              if (nodeAttrs["data-responsive-styles"]) {
                try {
                  const styleData = JSON.parse(nodeAttrs["data-responsive-styles"]);
                  if (styleData.padding) {
                    setters.setColumnPadding(styleData.padding);
                  }
                } catch {
                  // Use defaults
                }
              } else {
                // Try to parse padding from style string
                const parsedPadding = parsePaddingString(styleObj.padding);
                if (parsedPadding) {
                  setters.setColumnPadding({
                    xs: parsedPadding,
                    sm: parsedPadding,
                    md: parsedPadding,
                    lg: parsedPadding,
                    xl: parsedPadding,
                  });
                }
              }
              
              // Get text and vertical alignment
              const textAlign = nodeAttrs.textAlign || styleObj.textAlign || "left";
              const verticalAlign = nodeAttrs.verticalAlign || styleObj.verticalAlign || "middle";
              setters.setColumnTextAlign(textAlign as "left" | "center" | "right");
              setters.setColumnVerticalAlign(verticalAlign as "top" | "middle" | "bottom");
              
              // Get colors
              const bgColor = nodeAttrs.backgroundColor || styleObj.backgroundColor || "";
              const txtColor = nodeAttrs.textColor || styleObj.color || "";
              const brdColor = nodeAttrs.borderColor || styleObj.borderColor || "";
              
              // Store initial colors to track changes
              initialColorsRef.current.columnBackgroundColor = bgColor;
              initialColorsRef.current.columnTextColor = txtColor;
              initialColorsRef.current.columnBorderColor = brdColor;
              
              setters.setColumnBackgroundColor(bgColor);
              setters.setColumnTextColor(txtColor);
              setters.setColumnBorderColor(brdColor);
            }
            colIdx++;
          }
        });
      }
    } else {
      // No existing values - clear initial colors
      initialColorsRef.current.columnBackgroundColor = "";
      initialColorsRef.current.columnTextColor = "";
      initialColorsRef.current.columnBorderColor = "";
    }
    
    setters.setShowColumnStyleModal(true);
  }, [editor, setters]);

  const applyColumnStyles = useCallback(() => {
    if (!editor) return;
    
    const { selection } = editor.state;
    const { $from } = selection;
    
    // Find table
    let tableNode = null;
    let tablePos = 0;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "table") {
        tableNode = node;
        tablePos = $from.before(depth);
        break;
      }
    }
    
    if (!tableNode) {
      alert("Please place your cursor inside a table.");
      return;
    }
    
    const columnIndex = state.columnIndex;
    const uniqueClass = `col-${columnIndex}-style-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    
    // Store width data
    const widthData = JSON.stringify({
      widths: state.columnWidths,
      type: state.columnWidthType,
    });
    
    // Store responsive padding data
    const responsiveData = JSON.stringify({
      padding: state.columnPadding,
    });
    
    // Build base padding
    const basePadding = state.columnPadding.xs || defaultPadding;
    const paddingStr = `${basePadding.top}px ${basePadding.right}px ${basePadding.bottom}px ${basePadding.left}px`;
    
    // Apply styles to all cells in the column
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        const rows: Array<{ node: any; pos: number; cells: Array<{ node: any; pos: number }> }> = [];
        let currentRow: { node: any; pos: number; cells: Array<{ node: any; pos: number }> } | null = null;
        
        tableNode.descendants((node, pos) => {
          if (node.type.name === "tableRow") {
            currentRow = { node, pos, cells: [] };
            rows.push(currentRow);
          } else if ((node.type.name === "tableCell" || node.type.name === "tableHeader") && currentRow) {
            currentRow.cells.push({ node, pos });
          }
        });
        
        rows.forEach((row, rowIndex) => {
          if (row.cells[columnIndex]) {
            const cell = row.cells[columnIndex];
            const absolutePos = tablePos + cell.pos + 1;
            const isHeader = cell.node.type.name === "tableHeader";
            
            const attrs: Record<string, any> = {
              ...cell.node.attrs,
              class: uniqueClass,
              textAlign: state.columnTextAlign,
              verticalAlign: state.columnVerticalAlign,
              padding: paddingStr,
              "data-column-index": String(columnIndex),
              "data-responsive-styles": responsiveData,
            };
            
            // Only apply colors if they've been changed from initial values
            const bgChanged = state.columnBackgroundColor !== initialColorsRef.current.columnBackgroundColor;
            const txtChanged = state.columnTextColor !== initialColorsRef.current.columnTextColor;
            const brdChanged = state.columnBorderColor !== initialColorsRef.current.columnBorderColor;
            
            if (bgChanged && state.columnBackgroundColor) {
              attrs.backgroundColor = state.columnBackgroundColor;
            } else if (!bgChanged && cell.node.attrs.backgroundColor) {
              // Preserve existing color if not changed
              attrs.backgroundColor = cell.node.attrs.backgroundColor;
            }
            if (txtChanged && state.columnTextColor) {
              attrs.textColor = state.columnTextColor;
            } else if (!txtChanged && cell.node.attrs.textColor) {
              // Preserve existing color if not changed
              attrs.textColor = cell.node.attrs.textColor;
            }
            if (brdChanged && state.columnBorderColor) {
              attrs.borderColor = state.columnBorderColor;
            } else if (!brdChanged && cell.node.attrs.borderColor) {
              // Preserve existing color if not changed
              attrs.borderColor = cell.node.attrs.borderColor;
            }
            
            // Only store width data on header cells
            if (isHeader) {
              attrs["data-column-widths"] = widthData;
            }
            
            tr.setNodeMarkup(absolutePos, undefined, attrs);
          }
        });
        
        return true;
      })
      .run();
    
    // Generate CSS for responsive widths and padding
    let css = `
  .${uniqueClass} {
    padding: ${state.columnPadding.xs.top}px ${state.columnPadding.xs.right}px ${state.columnPadding.xs.bottom}px ${state.columnPadding.xs.left}px !important;
    text-align: ${state.columnTextAlign} !important;
    vertical-align: ${state.columnVerticalAlign} !important;`;
    
    if (state.columnWidthType !== "auto" && state.columnWidths.xs) {
      const unit = state.columnWidthType === "percent" ? "%" : "px";
      css += `
    width: ${state.columnWidths.xs}${unit} !important;`;
      if (state.columnWidthType === "pixel") {
        css += `
    min-width: ${state.columnWidths.xs}px !important;`;
      }
    }
    
    // Only add color CSS if colors have been changed
    const bgChanged = state.columnBackgroundColor !== initialColorsRef.current.columnBackgroundColor;
    const txtChanged = state.columnTextColor !== initialColorsRef.current.columnTextColor;
    const brdChanged = state.columnBorderColor !== initialColorsRef.current.columnBorderColor;
    
    if (bgChanged && state.columnBackgroundColor) {
      css += `
    background-color: ${state.columnBackgroundColor} !important;`;
    }
    if (txtChanged && state.columnTextColor) {
      css += `
    color: ${state.columnTextColor} !important;`;
    }
    if (brdChanged && state.columnBorderColor) {
      css += `
    border-color: ${state.columnBorderColor} !important;`;
    }
    
    css += `
  }`;
    
    const breakpoints = [
      { key: "sm", width: "1080px" },
      { key: "md", width: "1230px" },
      { key: "lg", width: "1430px" },
      { key: "xl", width: "1750px" },
    ] as const;
    
    breakpoints.forEach(({ key, width }) => {
      const bpPadding = state.columnPadding[key];
      const bpWidth = state.columnWidths[key];
      
      let hasStyles = false;
      let bpCss = `
  @media (min-width: ${width}) {
    .${uniqueClass} {`;
      
      if (bpPadding) {
        bpCss += `
      padding: ${bpPadding.top}px ${bpPadding.right}px ${bpPadding.bottom}px ${bpPadding.left}px !important;`;
        hasStyles = true;
      }
      
      if (state.columnWidthType !== "auto" && bpWidth) {
        const unit = state.columnWidthType === "percent" ? "%" : "px";
        bpCss += `
      width: ${bpWidth}${unit} !important;`;
        if (state.columnWidthType === "pixel") {
          bpCss += `
      min-width: ${bpWidth}px !important;`;
        }
        hasStyles = true;
      }
      
      bpCss += `
    }
  }`;
      
      if (hasStyles) {
        css += bpCss;
      }
    });
    
    // Add CSS to document
    let currentHtml = editor.getHTML();
    const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
    const styleMatch = currentHtml.match(styleTagRegex);
    
    const htmlWithStyles = styleMatch
      ? currentHtml.replace(styleTagRegex, `<style>${styleMatch[1]}${css}</style>`)
      : `<style>${css}</style>${currentHtml}`;
    
    editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
    const finalHtml = wrapImagesWithLinks(editor.getHTML());
    onChange?.(finalHtml);
    
    // Close modal first
    setters.setShowColumnStyleModal(false);
    
    // Re-focus the editor after a short delay to maintain selection state
    setTimeout(() => {
      editor.chain().focus().run();
    }, 100);
  }, [editor, state.columnIndex, state.columnWidths, state.columnWidthType, state.columnTextAlign, state.columnVerticalAlign, state.columnPadding, state.columnBackgroundColor, state.columnTextColor, state.columnBorderColor, setters, onChange]);

  // Responsive Cell Style Modal Handlers
  const openResponsiveCellStyleModal = useCallback(() => {
    if (!editor) return;
    
    // Try to get attributes from tableCell first, then tableHeader
    let cellAttrs = editor.getAttributes("tableCell");
    if (!cellAttrs || Object.keys(cellAttrs).length === 0) {
      cellAttrs = editor.getAttributes("tableHeader");
    }
    
    // Parse the style string to get current values
    const styleObj = parseStyleString(cellAttrs?.style);
    
    // Build current style values from attributes and style string
    const currentStyle = {
      backgroundColor: cellAttrs?.backgroundColor || styleObj.backgroundColor || "#ffffff",
      textColor: cellAttrs?.textColor || styleObj.color || "#000000",
      borderColor: cellAttrs?.borderColor || styleObj.borderColor || "#e5e7eb",
      borderWidth: cellAttrs?.borderWidth?.toString() || extractNumericValue(styleObj.borderWidth) || "1",
      fontSize: cellAttrs?.fontSize?.toString() || extractNumericValue(styleObj.fontSize) || "",
      fontWeight: cellAttrs?.fontWeight || styleObj.fontWeight || "",
      textAlign: (cellAttrs?.textAlign || styleObj.textAlign || "left") as "left" | "center" | "right",
      padding: parsePaddingString(styleObj.padding) || { ...defaultPadding },
    };
    
    const defaultStyles: ResponsiveCellStyle = {
      xs: { ...currentStyle },
      sm: { ...currentStyle },
      md: { ...currentStyle },
      lg: { ...currentStyle },
      xl: { ...currentStyle },
    };
    
    // Override with saved responsive breakpoints if available
    if (cellAttrs?.["data-responsive-styles"]) {
      try {
        const savedStyles = JSON.parse(cellAttrs["data-responsive-styles"]);
        if (savedStyles.breakpoints) {
          Object.keys(savedStyles.breakpoints).forEach((key) => {
            if (defaultStyles[key as keyof ResponsiveCellStyle] && savedStyles.breakpoints[key]) {
              defaultStyles[key as keyof ResponsiveCellStyle] = {
                ...defaultStyles[key as keyof ResponsiveCellStyle],
                ...savedStyles.breakpoints[key],
              };
            }
          });
        }
      } catch {
        // Use defaults
      }
    }
    
    setters.setResponsiveCellStyles(defaultStyles);
    setters.setShowResponsiveCellStyleModal(true);
  }, [editor, setters]);

  const applyResponsiveCellStyles = useCallback(() => {
    if (!editor) return;
    
    const uniqueClass = `responsive-cell-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    
    // Store responsive styles data
    const responsiveData = JSON.stringify({
      breakpoints: state.responsiveCellStyles,
    });
    
    // Apply base styles (xs breakpoint)
    const baseStyles = state.responsiveCellStyles.xs;
    const basePadding = baseStyles.padding || defaultPadding;
    const paddingStr = `${basePadding.top}px ${basePadding.right}px ${basePadding.bottom}px ${basePadding.left}px`;
    
    editor
      .chain()
      .focus()
      .setCellAttribute("class", uniqueClass)
      .setCellAttribute("backgroundColor", baseStyles.backgroundColor)
      .setCellAttribute("textColor", baseStyles.textColor)
      .setCellAttribute("borderColor", baseStyles.borderColor)
      .setCellAttribute("borderWidth", parseInt(baseStyles.borderWidth || "1"))
      .setCellAttribute("textAlign", baseStyles.textAlign)
      .setCellAttribute("fontSize", baseStyles.fontSize || null)
      .setCellAttribute("fontWeight", baseStyles.fontWeight || null)
      .setCellAttribute("padding", paddingStr)
      .setCellAttribute("data-responsive-styles", responsiveData)
      .run();
    
    // Generate CSS for all breakpoints
    const generateBreakpointStyles = (styles: typeof baseStyles) => {
      const pad = styles.padding || defaultPadding;
      let css = "";
      if (styles.backgroundColor) css += `background-color: ${styles.backgroundColor} !important; `;
      if (styles.textColor) css += `color: ${styles.textColor} !important; `;
      if (styles.borderColor) css += `border-color: ${styles.borderColor} !important; `;
      if (styles.borderWidth) css += `border-width: ${styles.borderWidth}px !important; `;
      if (styles.textAlign) css += `text-align: ${styles.textAlign} !important; `;
      if (styles.fontSize) css += `font-size: ${styles.fontSize}px !important; `;
      if (styles.fontWeight) css += `font-weight: ${styles.fontWeight} !important; `;
      css += `padding: ${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px !important;`;
      return css;
    };
    
    let css = `
  .${uniqueClass} {
    ${generateBreakpointStyles(state.responsiveCellStyles.xs)}
  }`;
    
    const breakpoints = [
      { key: "sm", width: "1080px" },
      { key: "md", width: "1230px" },
      { key: "lg", width: "1430px" },
      { key: "xl", width: "1750px" },
    ] as const;
    
    breakpoints.forEach(({ key, width }) => {
      const bpStyles = state.responsiveCellStyles[key];
      if (bpStyles) {
        css += `
  @media (min-width: ${width}) {
    .${uniqueClass} {
      ${generateBreakpointStyles(bpStyles)}
    }
  }`;
      }
    });
    
    // Add CSS to document
    let currentHtml = editor.getHTML();
    const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
    const styleMatch = currentHtml.match(styleTagRegex);
    
    const htmlWithStyles = styleMatch
      ? currentHtml.replace(styleTagRegex, `<style>${styleMatch[1]}${css}</style>`)
      : `<style>${css}</style>${currentHtml}`;
    
    editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
    const finalHtml = wrapImagesWithLinks(editor.getHTML());
    onChange?.(finalHtml);
    
    // Close modal first
    setters.setShowResponsiveCellStyleModal(false);
    
    // Re-focus the editor after a short delay to maintain selection state
    setTimeout(() => {
      editor.chain().focus().run();
    }, 100);
  }, [editor, state.responsiveCellStyles, setters, onChange]);

  const openResponsiveTableSizeModal = useCallback(() => {
    if (!editor) return;
    const tableAttrs = editor.getAttributes("table");
    if (tableAttrs["data-responsive-widths"]) {
      try {
        const savedData = JSON.parse(tableAttrs["data-responsive-widths"]);
        setters.setResponsiveTableWidths({
          xs: savedData.xs || "",
          sm: savedData.sm || "",
          md: savedData.md || "",
          lg: savedData.lg || "",
          xl: savedData.xl || "",
        });
        setters.setTableWidthType(savedData.type || "percent");
        setters.setEnableHorizontalScroll(savedData.horizontalScroll !== false);
        setters.setTableAlignment(savedData.alignment || "left");
      } catch {
        setters.setResponsiveTableWidths({ xs: "", sm: "", md: "", lg: "", xl: "" });
        setters.setTableWidthType("percent");
        setters.setEnableHorizontalScroll(true);
        setters.setTableAlignment("left");
      }
    } else {
      setters.setResponsiveTableWidths({ xs: "", sm: "", md: "", lg: "", xl: "" });
      setters.setTableWidthType("percent");
      setters.setEnableHorizontalScroll(true);
      setters.setTableAlignment("left");
    }
    setters.setShowResponsiveTableSize(true);
  }, [editor, setters]);

  const applyResponsiveTableWidth = useCallback(() => {
    if (!editor) return;

    const hasWidth = Object.values(state.responsiveTableWidths).some((v) => v);
    if (!hasWidth) {
      alert("Please select at least one table width.");
      return;
    }

    if (!editor.isActive("table")) {
      alert("Please place your cursor inside a table.");
      return;
    }

    const uniqueClass = `responsive-table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const unit = state.tableWidthType === "pixel" ? "px" : "%";
    const responsiveData = JSON.stringify({
      ...state.responsiveTableWidths,
      type: state.tableWidthType,
      horizontalScroll: state.enableHorizontalScroll,
      alignment: state.tableAlignment,
    });

    const baseWidth =
      state.responsiveTableWidths.xs ||
      state.responsiveTableWidths.sm ||
      state.responsiveTableWidths.md ||
      state.responsiveTableWidths.lg ||
      state.responsiveTableWidths.xl ||
      (state.tableWidthType === "pixel" ? "800" : "100");

    const tableAttrs = editor.getAttributes("table");
    const existingClass = tableAttrs.class || "";
    const cleanedClasses = existingClass
      .split(" ")
      .filter((cls: string) => cls && !cls.startsWith("responsive-table-"))
      .join(" ");
    const newClass = cleanedClasses ? `${cleanedClasses} ${uniqueClass}` : uniqueClass;

    editor
      .chain()
      .focus()
      .updateAttributes("table", {
        class: newClass,
        "data-responsive-widths": responsiveData,
        style: `width: ${baseWidth}${unit};`,
      })
      .run();

    const getAlignmentStyles = () => {
      switch (state.tableAlignment) {
        case "center":
          return "margin-left: auto !important; margin-right: auto !important;";
        case "right":
          return "margin-left: auto !important; margin-right: 0 !important;";
        default:
          return "margin-left: 0 !important; margin-right: auto !important;";
      }
    };

    const baseWidthValue = state.responsiveTableWidths.xs || baseWidth;
    let css = `
  .${uniqueClass} {
    width: ${baseWidthValue}${unit} !important;
    ${state.tableWidthType === "pixel" ? `min-width: ${baseWidthValue}px !important;` : `max-width: ${baseWidthValue}% !important;`}
    ${getAlignmentStyles()}
    ${state.enableHorizontalScroll ? `display: block; overflow-x: auto; -webkit-overflow-scrolling: touch;` : ""}
  }`;

    const breakpoints = [
      { key: "sm", width: "1080px" },
      { key: "md", width: "1230px" },
      { key: "lg", width: "1430px" },
      { key: "xl", width: "1750px" },
    ] as const;

    breakpoints.forEach(({ key, width }) => {
      if (state.responsiveTableWidths[key]) {
        css += `
  @media (min-width: ${width}) {
    .${uniqueClass} {
      width: ${state.responsiveTableWidths[key]}${unit} !important;
      ${state.tableWidthType === "pixel" ? `min-width: ${state.responsiveTableWidths[key]}px !important;` : `max-width: ${state.responsiveTableWidths[key]}% !important;`}
      ${getAlignmentStyles()}
    }
  }`;
      }
    });

    let currentHtml = editor.getHTML();
    const styleTagRegex = /<style>([\s\S]*?)<\/style>/;
    const styleMatch = currentHtml.match(styleTagRegex);

    const htmlWithStyles = styleMatch
      ? currentHtml.replace(styleTagRegex, `<style>${styleMatch[1]}${css}</style>`)
      : `<style>${css}</style>${currentHtml}`;

    editor.commands.setContent(htmlWithStyles, { emitUpdate: false });
    const finalHtml = wrapImagesWithLinks(editor.getHTML());
    onChange?.(finalHtml);

    setters.setShowResponsiveTableSize(false);
    setters.setResponsiveTableWidths({ xs: "", sm: "", md: "", lg: "", xl: "" });
    setters.setTableWidthType("percent");
    setters.setEnableHorizontalScroll(true);
    setters.setTableAlignment("left");
    
    // Re-focus the editor after a short delay to maintain selection state
    setTimeout(() => {
      editor.chain().focus().run();
    }, 100);
  }, [editor, state, setters, onChange]);

  const resetColumnWidths = useCallback(() => {
    if (!editor) return;
    if (!editor.isActive("table")) {
      alert("Please place your cursor inside a table.");
      return;
    }

    let currentHtml = editor.getHTML();
    currentHtml = currentHtml.replace(/\s*colwidth="[^"]*"/gi, "");
    currentHtml = currentHtml.replace(/<col\s+style="[^"]*width:\s*\d+px[^"]*">/gi, () => {
      return '<col style="min-width: 25px;">';
    });
    currentHtml = currentHtml.replace(/<col\s+style="width:\s*\d+px;">/gi, "<col>");

    editor.commands.setContent(currentHtml, { emitUpdate: false });
    const finalHtml = wrapImagesWithLinks(editor.getHTML());
    onChange?.(finalHtml);
  }, [editor, onChange]);

  return {
    insertTable,
    openTableSettings,
    applyTableSettings,
    openCellStyleModal,
    applyCellStyles,
    openResponsiveTableSizeModal,
    applyResponsiveTableWidth,
    resetColumnWidths,
    // New handlers
    openColumnStyleModal,
    applyColumnStyles,
    openResponsiveCellStyleModal,
    applyResponsiveCellStyles,
  };
};

